import { useEffect, useState, type ReactNode } from "react";
import { Link } from "wouter";
import { doc, onSnapshot } from "firebase/firestore";
import { paths, type LienNoticeItem } from "@clevis/shared-types";
import { db } from "../lib/firebase";
import { ShieldCheck } from "lucide-react";
import { Button } from "@clevis/shared-ui";
import { FRESH_SIGN_IN, useTwoStep } from "../lib/twoStep";
import { useAuth } from "../hooks/useAuth";
import { TwoStepSetup } from "./TwoStepSetup";

// Owners, admins and Clevis staff need two-step sign-in, without it getting in
// the way: right after signing in they're offered it once ("Not now" works
// until the date it's due), the dashboard carries a one-line reminder, and
// only after the due date does Clevis wait for it before anything else.

const freshSignIn = () => {
  try {
    return sessionStorage.getItem(FRESH_SIGN_IN) === "1";
  } catch {
    return false;
  }
};
const clearFresh = () => {
  try {
    sessionStorage.removeItem(FRESH_SIGN_IN);
  } catch {
    /* private window */
  }
};

export function TwoStepGate({ children }: { children: ReactNode }) {
  const { factors, required, dueAt, loaded } = useTwoStep();
  const { signOut } = useAuth();
  const [offer, setOffer] = useState(freshSignIn);
  // If two-step isn't switched on for Clevis, never hold anyone up.
  const [unavailable, setUnavailable] = useState(false);
  useEffect(() => {
    if (factors.length) clearFresh();
  }, [factors.length]);

  if (!required || factors.length || !loaded || unavailable) return <>{children}</>;
  const overdue = Date.now() >= dueAt;
  if (!overdue && !offer) return <>{children}</>;
  return (
    <div className="mx-auto max-w-xl space-y-3">
      <div>
        <h1 className="text-lg font-bold text-ink">Protect your company's account</h1>
        <p className="text-sm text-steel">
          {overdue
            ? "Owners and admins sign in with two steps: your password, then a code from your phone. Set it up to keep going; it takes two minutes."
            : "You can change pay, billing and who has access, so Clevis asks owners and admins for a code from their phone when they sign in on a new device. Devices you're signed in on aren't asked again."}
        </p>
      </div>
      <TwoStepSetup intro="Use an authenticator app (best), or have codes texted to your cell." onUnavailable={() => setUnavailable(true)} />
      <div className="flex items-center justify-between text-xs">
        {!overdue ? (
          <Button
            variant="ghost"
            className="px-3 py-1 text-xs"
            onClick={() => {
              clearFresh();
              setOffer(false);
            }}
          >
            Not now (needed by {new Date(dueAt).toLocaleDateString(undefined, { month: "long", day: "numeric" })})
          </Button>
        ) : (
          <span className="text-steel">Lost your phone? Your company's owner can reset it from the Team page.</span>
        )}
        <button className="text-steel underline hover:text-ink" onClick={() => void signOut()}>
          Sign out
        </button>
      </div>
    </div>
  );
}

/** One line on the dashboard until it's set up (owners, admins, Clevis staff). */
export function TwoStepNotice({ href = "/profile" }: { href?: string }) {
  const { factors, required, dueAt, loaded } = useTwoStep();
  if (!loaded || !required || factors.length) return null;
  return (
    <Link href={href} className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-xs text-ink ring-1 ring-steel/15 hover:ring-brandorange">
      <ShieldCheck size={15} className="shrink-0 text-brandorange" />
      <span className="flex-1">
        Turn on two-step sign-in: owners and admins need it by {new Date(dueAt).toLocaleDateString(undefined, { month: "long", day: "numeric" })}. Two minutes.
      </span>
      <span className="font-semibold text-brandorange-dark">Set up →</span>
    </Link>
  );
}

/** Dashboard: Nevada lien notices due within a week or past the 31 days (from the daily check). */
export function LienNoticesDue() {
  const { tenantId, role } = useAuth();
  const [items, setItems] = useState<Array<LienNoticeItem & { projectId: string; projectName: string }>>([]);
  useEffect(() => {
    if (!tenantId || role === "tech") return;
    return onSnapshot(
      doc(db, paths.tenantLienNotices(tenantId)),
      (s) => setItems(((s.get("items") as Array<LienNoticeItem & { projectId: string; projectName: string }> | undefined) ?? []).filter((i) => i.status === "due_soon" || i.status === "overdue")),
      () => setItems([]),
    );
  }, [tenantId, role]);
  if (!items.length) return null;
  return (
    <div className="rounded-lg bg-white px-3 py-2 text-xs ring-1 ring-brandorange/40">
      <p className="font-semibold text-ink">Lien notices to send</p>
      {items.slice(0, 5).map((i) => (
        <Link key={`${i.projectId}:${i.key}`} href={`/projects/${i.projectId}`} className="flex justify-between gap-2 py-0.5 hover:underline">
          <span className="truncate text-ink">
            {i.projectName} · {i.label}
          </span>
          <span className={i.status === "overdue" ? "shrink-0 font-semibold text-danger" : "shrink-0 text-brandorange-dark"}>
            {i.status === "overdue" ? "past 31 days" : `by ${new Date(i.dueAt!).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`}
          </span>
        </Link>
      ))}
    </div>
  );
}
